import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import GenericPage from '../../components/GenericPage';
import { 
  fetchCurrencies, 
  createCurrency, 
  updateCurrency, 
  deleteCurrency,
  setPagination,
  setSort,
  searchCurrencies,
  setDefaultFilter,
  changeCondition,
  addNewCondition,
  updateOptionRule,
  updateOptionValue,
  removeCondition,
  saveFilterCondition,
  setSelected,
  resetFiltersAndPagination
} from '../../store/modules/currency/currencyActions';
import CurrencyForm from '../../components/pageModule/Currencies/CurrencyForm';

const MySwal = withReactContent(Swal);

const Currencies = ({ excludedActions = [], onSelectedRowsChange = () => {} }) => {

  const currencyState = useSelector(state => state.currency);
  const dispatch = useDispatch();

  useEffect(() => {
    return () => {
      dispatch(resetFiltersAndPagination());
    };
  }, []);

  const handleCreate = async (formData) => {
    try {
      const response = await dispatch(createCurrency(formData));
      toast.success('Currency created successfully', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      return response;
    } catch (error) {
      toast.error(error.message || 'Error creating currency', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      throw error;
    }
  };

  const handleUpdate = async (id, formData) => {
    try {
      const response = await dispatch(updateCurrency(id, formData));
      toast.success('Currency updated successfully', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      return response;
    } catch (error) {
      toast.error(error.message || 'Error updating currency', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      throw error;
    }
  };

  const handleDelete = async (id) => {
    const result = await MySwal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'No, cancel!',
      reverseButtons: true
    });

    if (result.isConfirmed) {
      try {
        await dispatch(deleteCurrency(id));
        toast.success('Currency deleted successfully', {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
        return true;
      } catch (error) {
        toast.error(error.message || 'Error deleting currency', {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
        return false;
      }
    }
    return false; 
  };

  const columns = [
    {
      name: 'Code',
      selector: row => row.code,
      sortable: true,
      sortField: 'code',
    },
    {
      name: 'Conversion Rate',
      selector: row => row.conversion_rate,
      sortable: true,
      sortField: 'conversion_rate',
    },
  ];


  const actions = {
    fetch: (queryFilter, activeFilter, shouldUseFilter) => dispatch(fetchCurrencies(queryFilter, activeFilter, shouldUseFilter)),
    create: handleCreate,
    update: handleUpdate,
    delete: handleDelete,
    search: (search) => dispatch(searchCurrencies(search)),
    setPagination: (pagination) => dispatch(setPagination(pagination)),
    setSort: (sort) => dispatch(setSort(sort)),
    setDefaultFilter: (filter) => dispatch(setDefaultFilter(filter)),
    changeCondition: (index, condition) => dispatch(changeCondition(index, condition)),
    addNewCondition: (rule) => dispatch(addNewCondition(rule)),
    updateOptionRule: (index, rule) => dispatch(updateOptionRule(index, rule)),
    updateOptionValue: (index, value) => dispatch(updateOptionValue(index, value)),
    removeCondition: (index) => dispatch(removeCondition(index)),
    saveFilterCondition: (filter) => dispatch(saveFilterCondition(filter)),
    setSelected: (selectedRows) => dispatch(setSelected(selectedRows)),
  };

  const defaultAvailableActions = ['create', 'search', 'actions', 'edit'];
  const availableActions = defaultAvailableActions.filter(action => !excludedActions.includes(action));

  return (
    <>
      <GenericPage
        title="Currencies"
        moduleName="Currency"
        moduleKey="currencies"
        state={currencyState}
        actions={actions}
        FormComponent={CurrencyForm}
        columns={columns}
        availableActions={availableActions}
        actionsColumnWidth="150px"
        selectableRows={false}
        onSelectedRowsChange={onSelectedRowsChange}
      />
    </>
  );
};

export default Currencies; 